import { action, computed, makeObservable, observable } from "mobx";
import dayjs from "dayjs";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import errorToast from "../../../components/toast/ErrorToast";
import SaporeForm from "../../../forms/SaporeForm";
import DateField from "../../../forms/fields/DateField";

export default class SoldBoxesByCustomerAndArticleFeature {
    static TOTAL_ROW_ID = "TOTAL";
    static TOTAL_ROW_LABEL = "Total";
    static TOTAL_BOXES_COLUMN_ID = "totalBoxes";
    static TOTAL_BOXES_COLUMN_LABEL = "Total cajas";
    static DEFAULT_RANGE_IN_DAYS = 7;

    constructor(application) {
        this._application = application;
        this._loaded = false;
        this._tableLoaded = false;
        this._articles = [];
        this._rows = [];
        this._visibleRows = [];
        this._selectedArticleCategories = [];
        this._latestRequestNumber = 0;
        this._settingTheWholeDateRange = false;
        this._dateRangeBeforeTomorrowDelivery = this._defaultDateRange();
        this._filtersForm = this._createFiltersForm();

        makeObservable(this, {
            _loaded: observable,
            loaded: computed,
            _tableLoaded: observable,
            tableLoaded: computed,
            _articles: observable,
            _rows: observable,
            _visibleRows: observable,
            _selectedArticleCategories: observable,
            selectArticleCategories: action,
            setSoldBoxesByCustomerAndArticle: action,
            setVisibleRows: action,
            _finishLoading: action,
            _startLoadingTable: action,
            _finishLoadingTable: action,
        });
    }

    async load() {
        await this.loadSoldBoxesByCustomerAndArticle();
        this._finishLoading();
    }

    async loadSoldBoxesByCustomerAndArticle() {
        this._startLoadingTable();
        const requestNumber = this._registerNewRequest();
        const fromDate = this.fromDateField().value;
        const toDate = this.toDateField().value;
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                if (this._isTheLatestRequest(requestNumber)) {
                    this.setSoldBoxesByCustomerAndArticle(response.articles(), response.rows());
                }
            },
            handlesError: () => {
                if (this._isTheLatestRequest(requestNumber)) {
                    errorToast("No se pudieron obtener las cajas vendidas por cliente y artículo.");
                }
            },
        });
        const request = this._application.apiClient().getSoldBoxesByCustomerAndArticle(responseHandler, fromDate, toDate);
        return request.finally(() => this._finishLoadingTableFor(requestNumber));
    }

    _registerNewRequest() {
        this._latestRequestNumber += 1;
        return this._latestRequestNumber;
    }

    _isTheLatestRequest(requestNumber) {
        return requestNumber === this._latestRequestNumber;
    }

    setSoldBoxesByCustomerAndArticle(articles, rows) {
        this._articles = articles;
        this._rows = rows;
    }

    setVisibleRows(visibleRows) {
        this._visibleRows = visibleRows;
    }

    aValueChanged() {
        if (!this._settingTheWholeDateRange) {
            this.loadSoldBoxesByCustomerAndArticle();
        }
    }

    isShowingTomorrowDeliveryOnly() {
        const tomorrow = this._tomorrow();
        return this.fromDateField().value.isSame(tomorrow, "day") && this.toDateField().value.isSame(tomorrow, "day");
    }

    toggleTomorrowDeliveryOnly() {
        if (this.isShowingTomorrowDeliveryOnly()) {
            this._showDateRange(this._dateRangeBeforeTomorrowDelivery);
        } else {
            this._dateRangeBeforeTomorrowDelivery = this._currentDateRange();
            this._showDateRange(this._tomorrowDeliveryDateRange());
        }
    }

    _showDateRange(dateRange) {
        this._settingTheWholeDateRange = true;
        this.fromDateField().updateValue(dateRange.fromDate);
        this.toDateField().updateValue(dateRange.toDate);
        this._settingTheWholeDateRange = false;
        this.loadSoldBoxesByCustomerAndArticle();
    }

    _currentDateRange() {
        return { fromDate: this.fromDateField().value, toDate: this.toDateField().value };
    }

    _tomorrowDeliveryDateRange() {
        return { fromDate: this._tomorrow(), toDate: this._tomorrow() };
    }

    _defaultDateRange() {
        const today = dayjs().startOf("day");
        return { fromDate: today, toDate: today.add(SoldBoxesByCustomerAndArticleFeature.DEFAULT_RANGE_IN_DAYS, "day") };
    }

    _tomorrow() {
        return dayjs().startOf("day").add(1, "day");
    }

    get loaded() {
        return this._loaded;
    }

    get tableLoaded() {
        return this._tableLoaded;
    }

    filtersForm() {
        return this._filtersForm;
    }

    fromDateField() {
        return this.filtersForm().getFieldByName("fromDate");
    }

    toDateField() {
        return this.filtersForm().getFieldByName("toDate");
    }

    selectedArticleCategories() {
        return this._selectedArticleCategories;
    }

    selectArticleCategories(articleCategories) {
        this._selectedArticleCategories = articleCategories;
    }

    articleCategoryOptions() {
        const loadedCategories = this._articles.map((article) => article.category);
        const categories = new Set([...loadedCategories, ...this._selectedArticleCategories]);
        return [...categories]
            .sort((aCategory, anotherCategory) => aCategory.localeCompare(anotherCategory))
            .map((category) => ({ label: category, value: category }));
    }

    minimumSelectableDate() {
        return dayjs().startOf("day");
    }

    sortedArticles() {
        return this._articlesInSelectedCategories().sort((firstArticle, secondArticle) => firstArticle.name.localeCompare(secondArticle.name));
    }

    _articlesInSelectedCategories() {
        if (!this._isFilteringByArticleCategory()) {
            return [...this._articles];
        }
        return this._articles.filter((article) => this._selectedArticleCategories.includes(article.category));
    }

    _isFilteringByArticleCategory() {
        return this._selectedArticleCategories.length > 0;
    }

    columns() {
        return [
            { id: "customer", label: "Cliente" },
            { id: "createdBy", label: "Cargado por" },
            { id: SoldBoxesByCustomerAndArticleFeature.TOTAL_BOXES_COLUMN_ID, label: SoldBoxesByCustomerAndArticleFeature.TOTAL_BOXES_COLUMN_LABEL },
            ...this.sortedArticles().map((article) => ({ id: article.code, label: article.name })),
        ];
    }

    presentedRows() {
        const dataRows = this._rows.map((row, rowIndex) => this._presentRow(`${rowIndex}`, row.customer, row.createdBy, row.boxesByArticleCode));
        const shownRows = this._isFilteringByArticleCategory() ? dataRows.filter((row) => this._hasBoxes(row)) : dataRows;
        return [this.totalsRowFor(shownRows), ...shownRows];
    }

    _hasBoxes(presentedRow) {
        return presentedRow[SoldBoxesByCustomerAndArticleFeature.TOTAL_BOXES_COLUMN_ID] > 0;
    }

    totalsRowFor(visibleRows) {
        const totalBoxesByColumnId = Object.fromEntries(this.sortedArticles().map((article) => [
            article.code,
            visibleRows.reduce((totalBoxes, row) => totalBoxes + (row[article.code] || 0), 0),
        ]));
        return {
            id: SoldBoxesByCustomerAndArticleFeature.TOTAL_ROW_ID,
            customer: SoldBoxesByCustomerAndArticleFeature.TOTAL_ROW_LABEL,
            createdBy: "",
            [SoldBoxesByCustomerAndArticleFeature.TOTAL_BOXES_COLUMN_ID]: this._totalBoxesAcross(totalBoxesByColumnId),
            ...totalBoxesByColumnId,
        };
    }

    _presentRow(id, customer, createdBy, boxesByArticleCode) {
        const boxesByColumnId = Object.fromEntries(
            this.sortedArticles().map((article) => [article.code, boxesByArticleCode[article.code] || 0]));
        return {
            id,
            customer,
            createdBy,
            [SoldBoxesByCustomerAndArticleFeature.TOTAL_BOXES_COLUMN_ID]: this._totalBoxesAcross(boxesByColumnId),
            ...boxesByColumnId,
        };
    }

    _totalBoxesAcross(boxesByColumnId) {
        return Object.values(boxesByColumnId).reduce((totalBoxes, boxes) => totalBoxes + boxes, 0);
    }

    articleColumnNames() {
        return this.sortedArticles().map((article) => article.name);
    }

    rowsForExport() {
        return this._visibleDataRows().map((row) => ({
            customer: row.customer,
            loadedBy: row.createdBy,
            totalBoxes: row[SoldBoxesByCustomerAndArticleFeature.TOTAL_BOXES_COLUMN_ID],
            cells: this.sortedArticles().map((article) => row[article.code] || 0),
        }));
    }

    totalForExport() {
        const totalsRow = this.totalsRowFor(this._visibleDataRows());
        return {
            totalBoxes: totalsRow[SoldBoxesByCustomerAndArticleFeature.TOTAL_BOXES_COLUMN_ID],
            cells: this.sortedArticles().map((article) => totalsRow[article.code]),
        };
    }

    _visibleDataRows() {
        return this._visibleRows.filter((row) => row.id !== SoldBoxesByCustomerAndArticleFeature.TOTAL_ROW_ID);
    }

    _createFiltersForm() {
        const defaultDateRange = this._defaultDateRange();
        const fields = {
            fromDate: new DateField(false, defaultDateRange.fromDate, this),
            toDate: new DateField(false, defaultDateRange.toDate, this),
        };
        return new SaporeForm(fields);
    }

    _finishLoading() {
        this._loaded = true;
    }

    _startLoadingTable() {
        this._tableLoaded = false;
    }

    _finishLoadingTableFor(requestNumber) {
        if (this._isTheLatestRequest(requestNumber)) {
            this._finishLoadingTable();
        }
    }

    _finishLoadingTable() {
        this._tableLoaded = true;
    }
}
